// The only thing your main bundle imports: a floating button, and a lazy chat.
import { lazy, Suspense, useState, useRef, useCallback, useEffect } from "react";
import styled from "styled-components";
import { mobile } from "../utilities/responsive";

const Chat = lazy(() => import("./Chat"));

const Fab = styled.button`
  position: fixed;
  bottom: 38px;
  right: 28px;
  z-index: 1100;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 22px;
  border: none;
  border-radius: 999px;
  background: #111;
  color: #eeeeea;
  font-family: "Poppins", sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 38px rgba(0, 0, 0, 0.45), 0 0 24px rgba(153, 0, 0, 0.35);
  }

  &:focus-visible {
    outline: 2px solid #990000;
    outline-offset: 3px;
  }

  ${mobile({
    bottom: "18px",
    right: "18px",
    padding: "12px 18px",
    fontSize: "0.82rem",
  })}
`;

const PulseDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #990000;
  animation: aaiPulse 2.4s ease-in-out infinite;

  @keyframes aaiPulse {
    0% {
      box-shadow: 0 0 0 0 rgba(153, 0, 0, 0.55);
    }
    70% {
      box-shadow: 0 0 0 9px rgba(153, 0, 0, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(153, 0, 0, 0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    box-shadow: none;
  }
`;

export default function AskMyAI() {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const hasOpened = useRef(false);

  // Hand focus back where the visitor left it — but only after the button
  // remounts (the effect runs post-commit), and never on first page load.
  useEffect(() => {
    if (hasOpened.current && !open) buttonRef.current?.focus();
    hasOpened.current = true;
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      {!open && (
        <Fab ref={buttonRef} onClick={() => setOpen(true)} aria-haspopup="dialog">
          <PulseDot />
          Ask my AI
        </Fab>
      )}
      {open && (
        <Suspense fallback={null}>
          <Chat onClose={close} />
        </Suspense>
      )}
    </>
  );
}